import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Pagination, Response, formatBalance, formatDate } from '@ui/src';
import { StatementListItem } from '../../list/types';
import { statementsListApi } from '../services/list';
import { Category } from '../../../charts/types';

import ROUTES from '../../../../routes';

export type Filters = Record<string, string | string[]>;

export interface StatementsListState {
  statements?: StatementListItem[];
  selectedStatmenets?: string[];
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  filters?: Filters;
  pagination?: Pagination;
  accountCategories?: Category[];
  errorsList?: string;
}

const initialState: StatementsListState = {
  statements: undefined,
  selectedStatmenets: [],
  sort: undefined,
  filters: undefined,
};

export const statmenetsSlice = createSlice({
  name: 'statementsList',
  initialState,
  reducers: {
    clearStatementsListState: (state) => {
      state.statements = undefined;
      state.selectedStatmenets = [];
      state.pagination = undefined;
      state.errorsList = undefined;
    },
    selectStatements: (state, action: PayloadAction<string[]>) => {
      state.selectedStatmenets = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        statementsListApi.endpoints.getStatementsAccountsCategories
          .matchFulfilled,
        (state, action: PayloadAction<Response<Category>>) => {
          state.accountCategories = action.payload.data.edges.map(
            (item) => item.node,
          );
        },
      )
      .addMatcher(
        statementsListApi.endpoints.getStatementsList.matchPending,
        (state) => {
          state.errorsList = undefined;
        },
      )
      .addMatcher(
        statementsListApi.endpoints.getStatementsList.matchRejected,
        (state, action: any) => {
          state.statements = undefined;
          state.pagination = undefined;

          if (action?.payload.data) {
            if (Array.isArray(action?.payload.data?.message)) {
              state.errorsList = action?.payload.data?.message
                ?.map((item: { value: string }) => item.value)
                .join('; ');
            } else {
              state.errorsList = action?.payload.data?.error;
            }
          }
        },
      )
      .addMatcher(
        statementsListApi.endpoints.getStatementsList.matchFulfilled,
        (state, action: PayloadAction<Response<StatementListItem>>) => {
          const nextStatements = action.payload.data.edges.map((item) => ({
            ...item.node,
            counterpartyAccountName: `${
              item.node.counterpartyFirstName || ''
            } ${item.node.counterpartyLastName || ''}`,
            values: [],
            url: generatePath(ROUTES.TRANSACTIONS_GL, {
              transactionId: item.node.generalLedgerTransactionId as string,
            }),
          }));

          state.statements = nextStatements;

          state.pagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        statementsListApi.endpoints.getStatementsGL.matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: StatementListItem[] }> & {
            meta: {
              arg: {
                originalArgs: {
                  id: string;
                  query?: string | undefined;
                };
              };
            };
          },
        ) => {
          const nextStatementsGL = action.payload.data.map((item) => ({
            ...item,
            counterpartyAccountName: `${item.counterpartyFirstName || ''} ${
              item.counterpartyLastName || ''
            }`,
            transferDate: formatDate(item.transferDate || '', true),
            counterpartyAccountNumber: item.counterpartyAccountNumber || '',
            debitSum: item.debitSum ? formatBalance(item.debitSum) : '0.00',
            creditSum: item.creditSum ? formatBalance(item.creditSum) : '0.00',
            currencyId: item.currency?.name || '',
            balance: item.balance ? formatBalance(item.balance) : '—',
          }));

          state.statements = state.statements?.map((item) => {
            if (item.id === action.meta.arg.originalArgs.id) {
              return { ...item, values: nextStatementsGL };
            }
            return item;
          });
        },
      );
  },
});

export const { selectStatements, clearStatementsListState } =
  statmenetsSlice.actions;

export default statmenetsSlice.reducer;
